import React, { useState } from 'react';
import { parseWorkspaceSlugFromUrl } from '../../lib/workspaceUtils';
import {
  useNavigationService,
  useUserStore,
  useWorkspaceStore,
} from '../../stores';
import Avatar from './Avatar';

interface PageLayoutProps {
  icon?: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
  userAvatar?: {
    src?: string | null;
    alt?: string;
    birthday?: string | null;
  };
  copyableSubtitle?: boolean;
  onSubtitleCopy?: (text: string) => void;
}

const PageLayout: React.FC<PageLayoutProps> = ({
  icon,
  title,
  subtitle,
  children,
  className = '',
  userAvatar,
  copyableSubtitle = false,
  onSubtitleCopy,
}) => {
  const workspaceSlug = parseWorkspaceSlugFromUrl();
  const workspaceStore = useWorkspaceStore();
  const userStore = useUserStore();
  const navigationService = useNavigationService();
  const [copied, setCopied] = useState(false);

  const handleAvatarClick = () => {
    if (workspaceSlug) {
      navigationService.toProfile(workspaceSlug);
    }
  };

  const handleSubtitleCopy = async () => {
    if (!subtitle || !copyableSubtitle) return;

    try {
      // Try modern clipboard API first
      await navigator.clipboard.writeText(subtitle);
      setCopied(true);
      onSubtitleCopy?.(subtitle);

      // Reset copied state after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);

      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = subtitle;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);

        setCopied(true);
        onSubtitleCopy?.(subtitle);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy also failed:', fallbackErr);
      }
    }
  };

  // Show workspace name if available, otherwise fallback to slug
  const displayName =
    workspaceStore.workspaceName || workspaceSlug || 'Workspace';
  const iconUrl = workspaceStore.workspaceIconUrl;

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 ${className}`}
    >
      {/* Combined Header */}
      <div className="bg-gradient-to-b from-blue-600 to-purple-600 pt-4 pb-8">
        <div className="container mx-auto px-4">
          {/* Workspace Info Row */}
          <div className="flex items-center justify-between mb-6">
            {/* Left side - Workspace info */}
            <div className="flex items-center space-x-4 h-16">
              <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center border border-white/30 p-0.5">
                {iconUrl ? (
                  <img
                    src={iconUrl}
                    alt={`${displayName} icon`}
                    className="w-full h-full object-cover rounded-md"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.className =
                          'w-16 h-16 bg-white/30 rounded-lg flex items-center justify-center border border-white/30 p-0.5';
                        parent.innerHTML = `<span class="text-white text-sm font-semibold">${displayName.charAt(0).toUpperCase()}</span>`;
                      }
                    }}
                  />
                ) : (
                  <span className="text-white text-sm font-semibold">
                    {displayName.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div>
                <h2 className="text-base font-semibold text-white">
                  {displayName}
                </h2>
                <p className="text-xs text-white/60">Powered by Slotta</p>
              </div>
            </div>

            {/* Right side - User avatar */}
            <div className="flex items-center h-16">
              <button
                onClick={handleAvatarClick}
                className="focus:outline-none focus:ring-2 focus:ring-white/50 rounded-full transition-opacity hover:opacity-80"
              >
                <Avatar
                  src={userStore.user?.photoUrl}
                  alt={userStore.user?.name || 'User'}
                  size="md"
                  birthday={userStore.user?.birthday}
                  className="w-14 h-14 border-2 border-white/30 shadow-sm"
                />
              </button>
            </div>
          </div>

          {/* Page Info Row */}
          <div className="text-center">
            <div
              className={`flex items-center justify-center mb-4 mx-auto ${
                userAvatar
                  ? 'w-20 h-20'
                  : 'w-16 h-16 bg-white/20 rounded-2xl backdrop-blur-sm'
              }`}
            >
              {userAvatar ? (
                <Avatar
                  src={userAvatar.src}
                  alt={userAvatar.alt || 'User'}
                  size="xl"
                  birthday={userAvatar.birthday}
                  className="shadow-lg"
                />
              ) : icon ? (
                <span className="text-2xl">{icon}</span>
              ) : null}
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">{title}</h1>
            {subtitle && (
              <div className="mb-3">
                {copyableSubtitle ? (
                  <button
                    onClick={handleSubtitleCopy}
                    className="text-blue-100 text-base hover:text-white transition-colors duration-200 cursor-pointer flex items-center gap-2 mx-auto"
                    title="Click to copy"
                  >
                    <span>{subtitle}</span>
                    <span className="text-sm">{copied ? '✅' : '📋'}</span>
                  </button>
                ) : (
                  <p className="text-blue-100 text-base">{subtitle}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 -mt-8 pb-20">{children}</div>
    </div>
  );
};

export default PageLayout;
